import React, { useState, useEffect } from 'react';
import { X, CreditCard, Link, CheckCircle, XCircle, AlertTriangle, FileText, User } from 'lucide-react';
import apiClient from '../../api/config';
import { useToastContext } from '../common/Toast';
import { handleApiError } from '../../utils/errorHandler';
import { Invoice } from '../../types/invoice';

interface ProcessInvoicePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice;
  onSuccess?: () => void;
}

interface PaymentMethod {
  id: string;
  name: string;
  icon?: string;
  supported: boolean;
}

const ProcessInvoicePaymentModal: React.FC<ProcessInvoicePaymentModalProps> = ({
  isOpen,
  onClose,
  invoice,
  onSuccess
}) => {
  const { showSuccess, showError } = useToastContext();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string>('card');
  const [step, setStep] = useState<'method' | 'processing' | 'success' | 'error'>('method');
  const [paymentResult, setPaymentResult] = useState<any>(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
    }
  }, [isOpen]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await apiClient.get('/payments/methods');
      setPaymentMethods(response.data.data || []);
    } catch (error) {
      console.error('Failed to load payment methods:', error);
      // Set default payment methods if API fails
      setPaymentMethods([
        { id: 'card', name: 'Credit/Debit Card', supported: true },
        { id: 'knet', name: 'KNET', supported: true },
        { id: 'apple_pay', name: 'Apple Pay', supported: true },
        { id: 'google_pay', name: 'Google Pay', supported: true },
        { id: 'link', name: 'Payment Link', supported: true }
      ]);
    }
  };

  const handlePaymentLinkGeneration = async () => {
    setLoading(true);
    setStep('processing');

    try {
      const response = await apiClient.post('/payments/tap/payment-link', {
        invoice_id: invoice.id,
        amount: invoice.remaining_amount || invoice.total_amount,
        currency: invoice.currency || 'KWD',
        description: `Payment for Invoice ${invoice.invoice_number}`,
        customer: {
          name: invoice.customer?.name || 'Customer',
          phone: invoice.customer?.phone || '',
          email: invoice.customer?.email || '',
          country_code: '+965'
        }
      });

      if (response.data.payment_url) {
        setPaymentResult({
          ...response.data,
          type: 'link'
        });
        setStep('success');
        
        // Open payment URL in new tab
        window.open(response.data.payment_url, '_blank');
        
        showSuccess('Payment link generated successfully');
      } else {
        throw new Error('Failed to generate payment link');
      }
    } catch (error) {
      const processedError = handleApiError(error);
      setErrorMessage(processedError.message);
      setStep('error');
      showError(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectPayment = async () => {
    setLoading(true);
    setStep('processing');

    try {
      // For direct payment methods, we would integrate with TAP SDK here
      // This is a simplified version that creates a payment intent
      const response = await apiClient.post('/payments/tap/card-payment', {
        amount: invoice.remaining_amount || invoice.total_amount,
        currency: invoice.currency || 'KWD',
        invoice_id: invoice.id,
        customer_id: invoice.customer_id,
        source_id: 'src_card_placeholder', // In real implementation, this would come from TAP Card SDK
        description: `Payment for Invoice ${invoice.invoice_number}`
      });

      setPaymentResult({
        ...response.data,
        type: 'card'
      });
      setStep('success');
      showSuccess('Payment processed successfully');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const processedError = handleApiError(error);
      setErrorMessage(processedError.message);
      setStep('error');
      showError(processedError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayment = () => {
    if (selectedMethod === 'link') {
      handlePaymentLinkGeneration();
    } else {
      handleDirectPayment();
    }
  };

  const handleClose = () => {
    setStep('method');
    setPaymentResult(null);
    setErrorMessage('');
    setSelectedMethod('card');
    onClose();
  };

  const formatCurrency = (amount: number, currency: string = 'KWD') => {
    return new Intl.NumberFormat('ar-KW', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount);
  };

  const getPaymentMethodIcon = (methodId: string) => {
    switch (methodId) {
      case 'card':
        return <CreditCard className="h-5 w-5" />;
      case 'link':
        return <Link className="h-5 w-5" />;
      default:
        return <CreditCard className="h-5 w-5" />;
    }
  };

  if (!isOpen) return null;

  const paymentAmount = invoice.remaining_amount || invoice.total_amount;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center">
              <CreditCard className="h-5 w-5 text-teal-600 mr-2" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Process Payment
                </h3>
                <p className="text-sm text-gray-600">
                  Invoice #{invoice.invoice_number}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {step === 'method' && (
              <div className="space-y-6">
                {/* Invoice Summary */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center">
                      <FileText className="h-8 w-8 text-teal-600 mr-3" />
                      <div>
                        <h4 className="font-medium text-gray-900">
                          Invoice #{invoice.invoice_number}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(paymentAmount, invoice.currency)}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center text-sm text-gray-600">
                        <User className="h-4 w-4 mr-1" />
                        {invoice.customer?.name || 'N/A'}
                      </div>
                      <p className="text-sm text-gray-500">
                        {invoice.customer?.phone || 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Methods */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Select Payment Method
                  </h4>
                  <div className="space-y-2">
                    {paymentMethods.filter(method => method.supported).map((method) => (
                      <div
                        key={method.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedMethod === method.id
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedMethod(method.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            {getPaymentMethodIcon(method.id)}
                            <span className="font-medium">{method.name}</span>
                            {method.id === 'link' && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                Recommended
                              </span>
                            )}
                          </div>
                          <div className={`w-4 h-4 rounded-full border-2 ${
                            selectedMethod === method.id 
                              ? 'border-teal-500 bg-teal-500' 
                              : 'border-gray-300'
                          }`}>
                            {selectedMethod === method.id && (
                              <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Method Description */}
                <div className="bg-blue-50 p-3 rounded-lg">
                  <div className="flex items-start">
                    <AlertTriangle className="h-4 w-4 text-blue-600 mr-2 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      {selectedMethod === 'link' ? (
                        <p>A secure payment link will be generated and opened in a new tab. The customer can complete payment using various methods including KNET, credit cards, and digital wallets.</p>
                      ) : (
                        <p>Direct payment processing using {paymentMethods.find(m => m.id === selectedMethod)?.name}. The payment will be processed immediately through TAP Payments secure gateway.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {step === 'processing' && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Processing Payment...
                </h4>
                <p className="text-gray-600">
                  {selectedMethod === 'link' 
                    ? 'Generating secure payment link...' 
                    : 'Processing your payment securely...'}
                </p>
              </div>
            )}

            {step === 'success' && (
              <div className="text-center py-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {paymentResult?.type === 'link' ? 'Payment Link Generated!' : 'Payment Successful!'}
                </h4>
                <p className="text-gray-600 mb-4">
                  {paymentResult?.type === 'link' 
                    ? 'A secure payment link has been generated and opened in a new tab.'
                    : `Payment of ${formatCurrency(paymentAmount, invoice.currency)} has been processed successfully.`}
                </p>
                
                {paymentResult && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                    <div className="text-sm text-gray-600 space-y-1">
                      {paymentResult.payment_id && (
                        <p><strong>Payment ID:</strong> {paymentResult.payment_id}</p>
                      )}
                      {paymentResult.reference && (
                        <p><strong>Reference:</strong> {paymentResult.reference}</p>
                      )}
                      {paymentResult.payment_url && paymentResult.type === 'link' && (
                        <p><strong>Payment URL:</strong> <a href={paymentResult.payment_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Open Link</a></p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 'error' && (
              <div className="text-center py-8">
                <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Payment Failed
                </h4>
                <p className="text-gray-600 mb-4">
                  {errorMessage}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end space-x-3 p-4 border-t bg-gray-50">
            {step === 'method' && (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleProcessPayment}
                  className="px-4 py-2 text-white bg-teal-600 rounded-md hover:bg-teal-700 disabled:opacity-50 transition-colors flex items-center"
                  disabled={loading}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  {selectedMethod === 'link' ? 'Generate Payment Link' : 'Process Payment'}
                </button>
              </>
            )}
            
            {(step === 'success' || step === 'error') && (
              <>
                {step === 'error' && (
                  <button
                    onClick={() => setStep('method')}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Try Again
                  </button>
                )}
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-white bg-teal-600 rounded-md hover:bg-teal-700 transition-colors"
                >
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessInvoicePaymentModal;